import { Injectable } from '@angular/core';
import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { BehaviorSubject } from 'rxjs';
import { waitForMillis } from '@shared/delay.utils';

export type SpeechBoundary = {
  start: number;
  end: number;
  spokenWord: string;
};

/**
 * A service that handles speech synthesis operations.
 * It encapsulates the logic for initializing the speech synthesis, configuring voices,
 * and managing speech output for text, sentences, or words with pauses.
 */
@Injectable({
  providedIn: 'root',
})
export class SpeechSynthesisService {
  private utterance = 'SpeechSynthesisUtterance' in window ? new SpeechSynthesisUtterance() : undefined;
  private speechBoundarySubject = new BehaviorSubject<SpeechBoundary | null>(null);

  private lang = 'nl-NL';

  // Voices loaded signal
  private _voicesLoadedPromise: Promise<void>;
  private _voicesLoadedResolver: (() => void) | null = null;

  constructor() {
    // Initialize the voices loaded promise
    this._voicesLoadedPromise = new Promise<void>((resolve) => {
      this._voicesLoadedResolver = resolve;
    });
    this.init().then();
  }

  /**
   * Returns a promise that resolves when voices are loaded.
   */
  waitForVoicesLoaded(): Promise<void> {
    return this._voicesLoadedPromise;
  }

  /**
   * Initializes the speech synthesis utterance with the given text and language.
   * Also sets up the available voices.
   *
   * @param text - The text to be spoken.
   * @param lang - The language locale (default is 'nl-NL').
   */
  async init(lang: string = 'nl-NL') {
    if (this.utterance) this.utterance.lang = lang;
    this.lang = lang;

    await this.setupVoices();

    await TextToSpeech.addListener('onRangeStart', (data) => {
      this.speechBoundarySubject.next({
        start: data.start,
        end: data.end,
        spokenWord: data.spokenWord,
      });
    });
  }

  /**
   * Updates the configuration of the speech synthesis utterance.
   * You can update the voice, speaking rate, or language.
   *
   * @param config - An object containing optional voice, rate, and language configuration.
   */
  // eslint-disable-next-line no-undef
  updateConfig(config: { voice?: SpeechSynthesisVoice | null; rate?: number; lang?: string }): void {
    if (!this.utterance) {
      console.error('SpeechSynthesisUtterance is not available');
      return;
    }

    if (config.voice !== undefined) {
      this.utterance.voice = config.voice;
    }
    if (config.rate !== undefined) {
      this.utterance.rate = config.rate;
    }
    if (config.lang !== undefined) {
      this.utterance.lang = config.lang;
    }
  }

  /**
   * Speaks a sentence with optional callbacks for boundary events and when the speech ends.
   * The speech synthesis cancels any ongoing speech before speaking the provided sentence.
   *
   * @param sentence - The sentence to be spoken.
   * @param onBoundary - Optional callback function that gets called on word or sentence boundaries during speech.
   * @param onEnd - Optional callback function that gets called when the speech ends.
   * @param volume - The volume for speech synthesis (default is 1).
   */
  speakSentence(sentence: string, onBoundary?: (event: SpeechBoundary) => void, onEnd?: () => void, volume: number = 1) {
    return this.speakSentenceAsync(sentence, volume, { onBoundary, onEnd });
  }

  async speakSentenceAsync(
    sentence: string,
    volume: number = 1,
    options?: { onBoundary?: (event: SpeechBoundary) => void; onEnd?: () => void },
  ) {
    const sub = this.speechBoundarySubject.subscribe((data) => {
      if (data) {
        this.speechBoundarySubject.next(null);
        options?.onBoundary?.(data);
      }
    });

    await TextToSpeech.speak({
      text: sentence,
      volume,
      lang: this.lang,
    });

    sub.unsubscribe();
    options?.onEnd?.();
  }

  /**
   * Speaks an array of words sequentially with a pause between each word.
   * After each word is spoken, the onWordSpoken callback is executed.
   *
   * @param words - An array of words to be spoken.
   * @param pauseDuration - The duration of the pause (in milliseconds) between words.
   * @param onWordSpoken - Callback function that is called after each word is spoken.
   */
  async speakWords(words: string[], pauseDuration: number, onWordSpoken?: () => void) {
    if (words.length === 0) return;

    // stop talking
    await TextToSpeech.stop();

    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      await this.speakSentence(`${word}!`);
      onWordSpoken?.();

      if (i < words.length - 1) {
        await waitForMillis(pauseDuration);
      }
    }
  }

  /**
   * Cancels any ongoing speech synthesis.
   */
  async cancel() {
    this.speechBoundarySubject.next(null);
    await TextToSpeech.stop();
  }

  /**
   * Configures the voices available for speech synthesis.
   * Specifically, it sets the voice to 'Microsoft Fenna Online (Natural) - Dutch (Netherlands)' if available.
   */
  private async setupVoices() {
    const voices = await TextToSpeech.getSupportedVoices();
    console.log('Available voices:', JSON.stringify(voices));

    if (!this.utterance) {
      console.error('SpeechSynthesisUtterance is not available');
      return;
    }

    window.speechSynthesis.onvoiceschanged = () => {
      const voices = window.speechSynthesis.getVoices();
      const dutchVoices = voices.filter((voice) => voice.lang.includes('nl'));
      const fennaVoice = dutchVoices.find((voice) => voice.name === 'Microsoft Fenna Online (Natural) - Dutch (Netherlands)');
      if (fennaVoice) {
        this.utterance!.voice = fennaVoice;
        console.log('Fenna voice found and set:', fennaVoice.name);
      }
      // Signal that voices are loaded
      if (this._voicesLoadedResolver) {
        this._voicesLoadedResolver();
        this._voicesLoadedResolver = null; // Only resolve once
      }
    };
    // If voices are already loaded, trigger the event manually
    if (window.speechSynthesis.getVoices().length > 0) {
      window.speechSynthesis.onvoiceschanged!(null as any);
    }
  }
}
